import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Descope, useSession } from '@descope/react-sdk';
import { toast } from 'react-toastify';

const css = `
  .auth-root{
    --blue: #0072F5;
    --blue-dark: #0052B3;
    --ink: #0B0D10;
    --paper: #F6F4EF;
    --paper-dim: #ECE8DF;
    --line: rgba(11,13,16,0.12);
    background:var(--paper);
    color:var(--ink);
    font-family:'Inter',sans-serif;
    -webkit-font-smoothing:antialiased;
    min-height:100vh;
    display:flex;
    flex-direction:column;
  }
  .auth-root *{box-sizing:border-box;}
  .auth-root .wrap{max-width:1240px; margin:0 auto; padding:0 32px;}
  .auth-root a{color:inherit; text-decoration:none;}

  .auth-root header{
    position:fixed; top:0; left:0; right:0; z-index:90;
    background:rgba(246,244,239,0.92); backdrop-filter:blur(10px);
    border-bottom:1px solid var(--line);
  }
  .auth-root nav{
    display:flex; align-items:center; justify-content:space-between;
    padding:20px 32px; max-width:1240px; margin:0 auto;
  }
  .auth-root .logo{font-weight:800; font-size:18px; letter-spacing:-0.02em;}
  .auth-root .logo span{color:var(--blue);}
  .auth-root .nav-links{display:flex; gap:36px; font-size:14px; font-weight:500;}
  .auth-root .nav-links a{opacity:.7; transition:opacity .2s;}
  .auth-root .nav-links a:hover{opacity:1;}
  .auth-root .nav-actions{display:flex; align-items:center; gap:22px;}
  .auth-root .nav-login{font-size:14px; font-weight:600; opacity:.75; transition:opacity .2s;}
  .auth-root .nav-login:hover{opacity:1;}
  .auth-root .nav-cta{
    font-size:14px; font-weight:600; padding:10px 20px;
    border:1px solid var(--ink); border-radius:2px;
    transition:background .2s, color .2s;
  }
  .auth-root .nav-cta:hover{background:var(--ink); color:var(--paper);}
  .auth-root .nav-toggle{display:none; background:none; border:none; cursor:pointer; padding:8px; margin-left:8px;}
  .auth-root .nav-toggle span{display:block; width:20px; height:2px; background:var(--ink); margin:4px 0;}

  .auth-root main{
    flex:1; display:flex; align-items:center; justify-content:center;
    padding:160px 32px 80px;
  }
  .auth-root .auth-card{
    width:100%; max-width:480px; border:1px solid var(--line); border-radius:8px;
    background:#fff; padding:40px; box-shadow:0 16px 40px -16px rgba(11,13,16,0.12);
  }
  .auth-root .auth-card-head{text-align:center; margin-bottom:28px;}
  .auth-root .auth-card-head h1{
    font-size:26px; font-weight:800; letter-spacing:-0.01em; margin:0 0 8px;
  }
  .auth-root .auth-card-head p{font-size:15px; color:#52555a; margin:0;}
  .auth-root .auth-legal{
    margin-top:24px; font-size:13px; text-align:center; color:#6b6e73; line-height:1.5;
  }
  .auth-root .auth-legal a{color:var(--blue-dark); font-weight:600;}
  .auth-root .auth-legal a:hover{text-decoration:underline;}
  .auth-root .auth-loading{
    flex:1; display:flex; align-items:center; justify-content:center;
  }
  .auth-root .spinner{
    width:40px; height:40px; border-radius:50%;
    border:3px solid var(--line); border-top-color:var(--blue);
    animation:auth-spin 0.8s linear infinite;
  }
  @keyframes auth-spin{ to{ transform:rotate(360deg); } }

  .auth-root footer{padding:64px 0 32px; border-top:1px solid var(--line);}
  .auth-root .foot-cols{display:grid; grid-template-columns:1.6fr 1fr 1fr; gap:40px; padding-bottom:48px; border-bottom:1px solid var(--line); margin-bottom:24px;}
  .auth-root .foot-brand p{font-size:14px; color:#6b6e73; margin-top:14px; max-width:260px; line-height:1.55;}
  .auth-root .foot-col-title{font-size:12px; font-weight:600; letter-spacing:.08em; text-transform:uppercase; color:#6b6e73; margin-bottom:16px;}
  .auth-root .foot-col a{display:block; font-size:14px; color:#52555a; padding:6px 0; transition:color .2s;}
  .auth-root .foot-col a:hover{color:var(--ink);}
  .auth-root .foot-bottom{font-size:13px; color:#6b6e73;}

  @media (max-width:860px){
    .auth-root main{padding-top:188px;}
    .auth-root .auth-card{padding:28px;}
    .auth-root .foot-cols{grid-template-columns:1fr; gap:32px;}

    .auth-root nav{flex-wrap:wrap; row-gap:14px;}
    .auth-root .nav-toggle{display:flex; flex-direction:column; justify-content:center; gap:5px; order:2; margin-left:auto;}
    .auth-root .nav-actions{order:3;}
    .auth-root .nav-links{
      display:none; order:4; width:100%; flex-direction:column; gap:0;
      border-top:1px solid var(--line); padding-top:8px;
    }
    .auth-root .nav-links.open{display:flex;}
    .auth-root .nav-links a{padding:14px 0; border-bottom:1px solid var(--line); opacity:1;}
  }
`;

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isSessionLoading } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  const redirectUrl = process.env.NODE_ENV === 'development'
    ? 'http://localhost:3000/auth'
    : 'https://databackfill.com/auth';

  useEffect(() => {
    const code = searchParams.get('code');
    const flow = searchParams.get('descope-login-flow');

    if (code && flow) {
      console.log('Received OAuth callback with code:', code);

      if (!isAuthenticated) {
        localStorage.removeItem('sessionToken');
      }

      if (isAuthenticated) {
        console.log('Successfully authenticated after OAuth callback');
        navigate('/dashboard');
      }
    } else if (isAuthenticated && !isSessionLoading) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, isSessionLoading, navigate, searchParams]);

  const handleSuccess = async (e) => {
    console.log(e);
    try {
      console.log('Authentication successful:', {
        userId: e.detail.user.userId,
        email: e.detail.user.email
      });
      localStorage.setItem('databackfill-useremail', e.detail.user.email);

      const sessionToken = e.detail.sessionJwt;
      if (sessionToken) {
        localStorage.setItem('sessionToken', sessionToken);
        localStorage.setItem('createdTime', e.detail.user.createdTime);
        console.log('Session token stored');
        navigate('/dashboard');
      } else {
        console.error('No session token received');
        toast.error('Authentication failed. Please try again.');
      }
    } catch (error) {
      console.error('Error in handleSuccess:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const handleError = (error) => {
    console.error('Authentication Error:', error);
    toast.error('Authentication failed. Please try again.');
  };

  const Header = (
    <header>
      <nav>
        <Link to="/" className="logo">Data<span>Backfill</span></Link>
        <button
          className="nav-toggle"
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span></span><span></span><span></span>
        </button>
        <div className="nav-actions">
          <Link to="/auth" className="nav-login">Log in</Link>
          <Link to="/contact" className="nav-cta">Start a Project</Link>
        </div>
        <div className={`nav-links${menuOpen ? ' open' : ''}`}>
          <Link to="/#services" onClick={closeMenu}>Services</Link>
          <Link to="/#work" onClick={closeMenu}>Case Studies</Link>
          <Link to="/#process" onClick={closeMenu}>How we work</Link>
          <Link to="/#team" onClick={closeMenu}>Team</Link>
          <Link to="/#pricing" onClick={closeMenu}>Pricing</Link>
        </div>
      </nav>
    </header>
  );

  const Footer = (
    <footer>
      <div className="wrap foot-cols">
        <div className="foot-brand">
          <div className="logo">Data<span>Backfill</span></div>
          <p>A boutique AI & data delivery studio: senior engineers, architects, and product designers shipping production systems.</p>
        </div>
        <div className="foot-col">
          <div className="foot-col-title">Services</div>
          <Link to="/#services">Claude Implementation</Link>
          <Link to="/#services">RAG & Knowledge Systems</Link>
          <Link to="/#services">Agentic Workflows</Link>
          <Link to="/#services">Data Infrastructure</Link>
        </div>
        <div className="foot-col">
          <div className="foot-col-title">Company</div>
          <Link to="/#work">Case Studies</Link>
          <Link to="/#team">Team</Link>
          <Link to="/#pricing">Pricing</Link>
          <Link to="/blog">Blog</Link>
          <Link to="/contact">Contact</Link>
        </div>
      </div>
      <div className="wrap foot-bottom">
        <div>© 2026 DataBackfill. All rights reserved.</div>
      </div>
    </footer>
  );

  if (isSessionLoading) {
    return (
      <div className="auth-root">
        <style>{css}</style>
        {Header}
        <div className="auth-loading">
          <div className="spinner"></div>
        </div>
        {Footer}
      </div>
    );
  }

  return (
    <div className="auth-root">
      <style>{css}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
      <link
        href="https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;1,6..72,400&family=Inter:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />

      {Header}

      <main>
        <div className="auth-card">
          <div className="auth-card-head">
            <h1>Welcome!</h1>
            <p>Get started with DataBackfill</p>
          </div>

          <Descope
            flowId="sign-up-or-in"
            theme="light"
            onSuccess={handleSuccess}
            onError={handleError}
            telemetryDisabled={true}
            redirectUrl={redirectUrl}
            persistence={true}
            autoRefresh={true}
          />

          <p className="auth-legal">
            By continuing, you agree to our{' '}
            <Link to="/terms">Terms of Use</Link>{' '}
            and{' '}
            <Link to="/privacypolicy">Privacy Policy</Link>
          </p>
        </div>
      </main>

      {Footer}
    </div>
  );
};

export default AuthPage;
